import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';

/**
 * Rotating status line for the long (~15-30s) Claude insight calls. The wait is
 * real work, not a hang — so instead of one static "Loading…" we cycle a list of
 * playful, baseball-flavored messages with a crossfade. Keeps the coach engaged
 * and signals "we're working" the whole time.
 *
 * Used by the GameChanger import ("working" step) and Smart Scout (loading state).
 */

// Baseball-dugout flavored. Order is shuffled once per mount so it never feels
// scripted, and we never repeat a phrase until the list is exhausted.
const DEFAULT_MESSAGES = [
  'Packaging your game data…',
  'Crunching the numbers…',
  'Parsing your xOVR…',
  'Giving Scout the steal sign…',
  'Throwing from the windup…',
  'Asking Blue for time…',
  'Stacking hands on 3…',
  'Reading the third-base coach…',
  'Checking the lineup card…',
  'Calling it from the dugout…',
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface Props {
  /** Override the phrase list (defaults to the baseball set above). */
  messages?: string[];
  /** Milliseconds each phrase stays up before the crossfade. */
  intervalMs?: number;
  className?: string;
}

export function RotatingStatus({ messages, intervalMs = 2600, className }: Props) {
  // Shuffle once per mount. Cycle through; when we run out, reshuffle.
  const orderRef = useRef<string[]>(shuffle(messages && messages.length ? messages : DEFAULT_MESSAGES));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex(prev => {
        const next = prev + 1;
        if (next >= orderRef.current.length) {
          orderRef.current = shuffle(orderRef.current);
          return 0;
        }
        return next;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return (
    <div className={className} aria-live="polite">
      <AnimatePresence mode="wait">
        <motion.p
          key={index}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="text-xs text-ink-2 max-w-xs text-center"
        >
          {orderRef.current[index]}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
